import React from 'react';
import { Seo, breadcrumbSchema, webPageSchema } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { ArrowUpRight } from 'lucide-react';

const STATS = [
  { n: '50+', l: 'Curated Award Categories' },
  { n: '300+', l: 'Industry Leaders & Decision-Makers' },
  { n: '30+', l: 'Brands' },
  { n: '10', l: 'Award Pillars Across Marketing Excellence' },
];

const About = () => (
  <div data-testid="page-about">
    <Seo
      title="About AdAura Awards 2026 · India's Premier Marketing Awards"
      description="About the AdAura Awards — India's most prestigious marketing awards presented by Marketing Mind. Learn our mission, jury, timeline and celebration of advertising, digital, brand, social, PR and startup excellence."
      path="/about"
      jsonLd={[
        webPageSchema({ path: '/about', name: 'About', description: "About the AdAura Awards — India's most prestigious marketing awards presented by Marketing Mind. Learn our mis" }),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
      ]}
    />
    <PageHeader
      title="About Us"
      italicWords={['Us']}
    />

    {/* WELCOME - editorial layout: copy on left, IG phone screenshot on right */}
    <section className="pb-20 md:pb-24">
      <div className="content-container">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <div className="eyebrow mb-5">Welcome To</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-[2.6rem] tracking-[-0.015em] leading-[1.1]">
                Marketing <span className="text-[#C9A56C]">Mind</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="font-sans text-white/65 text-[15px] md:text-base leading-[1.85] mt-8 max-w-2xl">
                Started in 2017, Marketing Mind is India&rsquo;s leading online media publication
                in the advertising, marketing and media industry. With a collective user base of
                over 5 million, we have positioned ourselves as a key platform for both audience
                and brands - creating content that covers global advertising, the latest
                campaigns, brand news and interactive marketing ideas, industry stories, case
                studies, social media insights, and the marketing trends shaping the world.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
            <Reveal delay={260}>
              <figure
                className="relative w-[200px] md:w-[230px]"
                data-testid="about-instagram-figure"
              >
                {/* Corner ticks - editorial frame */}
                <span aria-hidden className="absolute -top-2 -left-2 w-3 h-3 border-t border-l border-[#C9A56C]/60" />
                <span aria-hidden className="absolute -top-2 -right-2 w-3 h-3 border-t border-r border-[#C9A56C]/60" />
                <span aria-hidden className="absolute -bottom-2 -left-2 w-3 h-3 border-b border-l border-[#C9A56C]/60" />
                <span aria-hidden className="absolute -bottom-2 -right-2 w-3 h-3 border-b border-r border-[#C9A56C]/60" />

                {/* Image - tuned for the light editorial palette */}
                <div className="relative bg-white border border-[#3A2418]/55 p-[6px] overflow-hidden shadow-[0_18px_44px_-22px_rgba(58,36,24,0.35)]">
                  <img
                    src="/about/instagram-marketing-mind.jpg"
                    alt="Marketing Mind on Instagram — India's leading marketing publication"
                    loading="lazy"
                    decoding="async"
                    width="1080"
                    height="1080"
                    data-testid="about-instagram-image"
                    className="w-full h-auto block"
                  />
                </div>
              </figure>
            </Reveal>
          </div>
        </div>
      </div>
    </section>


    {/* MISSION */}
    <section className="section-pad bg-[#634F3A]">
      <div className="content-container">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <div className="eyebrow mb-5">Our Mission</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-[2.4rem] tracking-[-0.015em] leading-[1.12]">
                To amplify the voices that <span className="text-[#C9A56C]">leave a lasting impression.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={200}>
              <p className="font-sans text-white/60 text-[15px] md:text-base leading-[1.85]">
                To assist brands and industry experts in amplifying their message and leaving a
                lasting impression through measurable results and compelling content - while
                helping our audience stay on top of the game with the latest updates and
                trends across the marketing and advertising landscape.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>


    {/* VISION */}
    <section className="section-pad">
      <div className="content-container">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <Reveal><div className="eyebrow mb-5">Our Vision</div></Reveal>
            <Reveal delay={120}>
              <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-[2.4rem] tracking-[-0.015em] leading-[1.12]">
                A world where authenticity, engagement and <span className="text-[#C9A56C]">impact thrive.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={200}>
              <p className="font-sans text-white/60 text-[15px] md:text-base leading-[1.85]">
                To provide a platform that opens access to marketing and advertising knowledge
                from across the globe - and to build a digital world where authenticity,
                engagement and real impact are the things that genuinely thrive.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>


    {/* STATS */}
    <section className="section-pad bg-[#634F3A]">
      <div className="content-container">
        <Reveal>
          <div className="eyebrow mb-6">The Evening, In Numbers</div>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-[2.4rem] tracking-[-0.015em] leading-[1.12] max-w-3xl">
            A platform built at <span className="text-[#C9A56C]">scale and depth.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A56C]/15" data-testid="about-stats">
          {STATS.map((s) => (
            <Reveal key={s.l} className="bg-[#5A4733] p-8 md:p-9 min-h-[180px] flex flex-col justify-between">
              <div className="numeral text-4xl md:text-5xl">{s.n}</div>
              <div className="mt-6 text-[11px] tracking-[0.25em] uppercase text-[#C9A56C] leading-relaxed">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default About;
